import React, { type ChangeEvent, type KeyboardEvent, useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react';

import type { AuthorizationData } from 'app/modules/SigningNew/types';

import { VALUE_ENTER } from 'app/constants/keyValues';
import { FormControl } from 'app/design-system/components/FormControl';
import PasswordInput from 'app/design-system/components/Input/PasswordInput';
import { Modal } from 'app/design-system/components/Modal';
import { MIN_LEN_PHONE_NUMBER } from 'app/design-system/components/PhoneInput/constants';
import { getDialCode, removeNotNumberCharacter } from 'app/design-system/components/PhoneInput/phone-input-helpers';
import { useTranslation } from 'app/lang';
import { loginAllTabs } from 'app/lib/auth/authChannel';
import usePhoneData from 'app/lib/region/usePhoneData';
import { createAuthQuery } from 'app/modules/SigningNew/utils/auth-modals-query';
import { getIsLoginSuccess, processing, selectLoginError } from 'app/redux/selectors/auth';
import PhoneInput from 'app/widgets/InputsWithAnalytics/PhoneInput';

import { AuthContext } from '../../../context';
import { useAuthResult } from '../../../hooks/useAuthResult';
import { authorizationValidate } from '../../../utils/validation/authorization-validate';
import { SigningTypeSwitcher } from '../../SigningTypeSwitcher';

import dictionary from './AuthorizationBody.dictionary.json';

import styles from './AuthorizationBody.module.scss';

export type FormValues = {
  phoneNumber: string;
  password: string;
};

const initialValues: FormValues = {
  phoneNumber: '',
  password: '',
};

export const AuthorizationBodyWithFP = () => {
  const { t } = useTranslation();
  const { authData, setAuthData } = useContext(AuthContext);
  const { replace } = useHistory();
  const dispatch = useDispatch();
  const phoneData = usePhoneData();
  const { successHandler, errorHandler } = useAuthResult();
  const { data } = useVisitorData({}, { immediate: true });

  const {
    values,
    errors,
    handleChange,
    setFieldValue,
    setSubmitting,
    setErrors,
    handleSubmit,
  } = useFormik({
    initialValues,
    validateOnChange: false,
    validate: (
      values: FormValues,
    ) => authorizationValidate(t, values, authData, setAuthData),
    onSubmit: (values: FormValues) => {
      const { password, phoneNumber } = values;
      const username = phoneNumber ? removeNotNumberCharacter(phoneNumber) : '';
      dispatch(loginAllTabs({ username: username.trim(), password, requestId: data?.requestId }));
    },
  });

  const authLoading = useSelector(processing);
  const isAuthSuccess = useSelector(getIsLoginSuccess);
  const authError = useSelector(selectLoginError);

  const [newData, setNewData] = React.useState<AuthorizationData>({ ...authData });

  const settingValue = (prevState: AuthorizationData) => ({ ...prevState, ...newData });

  useEffect(() => {
    if (isAuthSuccess) {
      successHandler();
    }

    if (authError) {
      setSubmitting(false);
      errorHandler();
    }
  }, [isAuthSuccess, authError]);

  useEffect(() => {
    const initialPhone = sessionStorage.getItem('phone') ?? phoneData.dialCode;

    setFieldValue('phoneNumber', initialPhone, false);
  }, []);

  useEffect(() => {
    setNewData((prevState) => {
      const newData = {
        ...prevState,
        isEmptyField: values.phoneNumber === '' || values.password === '',
        phone: values.phoneNumber,
      };

      return newData;
    });
  }, [values]);

  useEffect(() => {
    setAuthData((prevState: AuthorizationData) => ({
      ...prevState,
      ...newData,
    }));
  }, [newData]);

  const handleOnChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = removeNotNumberCharacter(event.target.value);
    const currentNumber = removeNotNumberCharacter(values.phoneNumber);
    const newNumber = value.slice(currentNumber.length);

    // Todo https://jira.tennisi.it/browse/WEB-1654
    const isPhoneNumberLengthValid = value.length - currentNumber.length >= MIN_LEN_PHONE_NUMBER;

    if (isPhoneNumberLengthValid) {
      const currentNumberDialCode = getDialCode(currentNumber);
      const newNumberDialCode = getDialCode(newNumber);

      if (currentNumberDialCode === newNumberDialCode) {
        setAuthData((prevState) => ({ ...prevState, phone: newNumber }));
        setFieldValue('phoneNumber', newNumber);
        sessionStorage.setItem('phone', newNumber);

        return;
      }
    }

    setAuthData((prevState) => ({ ...prevState, phone: value }));
    setFieldValue('phoneNumber', value);
    sessionStorage.setItem('phone', value);
  }, [values.phoneNumber]);

  const handleFieldFocus = useCallback(() => {
    if (authData.showFieldError) {
      setNewData((prevState) => ({ ...prevState, showFieldError: newData.shake = false }));
      setAuthData(settingValue);
    }

    setErrors({});
  }, [authData.showFieldError]);

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === VALUE_ENTER) {
      handleSubmit();
      event.currentTarget.blur();
    }
  }, [handleSubmit]);

  const handleForgotPassword = useCallback(() => {
    replace({ search: createAuthQuery('restore') });
  }, []);

  const disabled = authLoading
  || authData.shake
  || authData.loginDataChecked
  || authData.telegramProcessing;
  const error = !!errors.phoneNumber || authData.showFieldError;

  const ForgetPassword = (
    <button data-test="ModalLogin-button-forgotPassword"
            data-ga-event="logInPasswordRestore"
            data-ga-event-value="headerMenuRestore"
            className={styles.forgotPassword}
            onClick={handleForgotPassword}>
      {t(dictionary.buttonTextForgotPassword)}
    </button>
  );

  const errorMessageClassNames = classNames(styles.errorMessage, {
    [styles.showErrorMessage]: authData.errorMessage,
  });

  return (
    <Modal.Body className={styles.container}>
      <SigningTypeSwitcher />
      <form id="authorizationForm"
            noValidate
            onSubmit={handleSubmit}
            data-test="ModalLogin-form">
        <ul className={styles.inputList}>
          <li data-test="ModalLogin-phoneFormGroup">
            <FormControl label= {t(dictionary.phoneInputLabel)}
                         data-test="ModalLogin-loginLabel">
              <PhoneInput name="phoneNumber"
                          value={values.phoneNumber}
                          placeholder={phoneData.placeholder}
                          onChange={handleOnChange}
                          error={error}
                          autoComplete="on"
                          disabled={disabled}
                          autoFocus
                          onKeyDown={onKeyDown}
                          onFocus={handleFieldFocus}
                          data-ga-event="logInNumberInput"
                          data-test="Modal-restorePassword-phoneInput" />
            </FormControl>
          </li>

          <li data-test="ModalLogin-passwordFormGroup">
            <FormControl label={t(dictionary.passwordLabel)}
                         linkNote={ForgetPassword}
                         data-test="ModalLogin-passwordLabel">
              <PasswordInput id="formHorizontalPassword"
                             name="password"
                             placeholder={t(dictionary.passwordPlaceholder)}
                             error={error}
                             value={values.password}
                             onChange={handleChange}
                             onFocus={handleFieldFocus}
                             disabled={disabled}
                             onKeyDown={onKeyDown}
                             className={styles.passwordInput}
                             data-ga-event="logInPasswordInput"
                             data-test="ModalLogin-passwordInput" />
            </FormControl>
          </li>
          <span className={errorMessageClassNames}>
            {t(dictionary.errorText)}
          </span>
        </ul>
      </form>
    </Modal.Body>
  );
};
